import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Heart, Pill, Moon, Activity, Utensils, Smile,
  AlertTriangle, CheckCircle,
  RefreshCw, Shield, ChevronDown, ChevronUp, Bell
} from 'lucide-react';

const API = import.meta.env.VITE_API_URL;

const ROLE_LABELS: Record<string, string> = {
  FAMILY_MEMBER: 'Membru Familie',
  CAREGIVER: 'Îngrijitor',
  DOCTOR: 'Medic',
  SUPPORT_PERSON: 'Persoană de Suport',
};

const SEVERITY_CONFIG: Record<string, { color: string; bg: string; border: string; label: string }> = {
  LOW: { color: 'text-green-700', bg: 'bg-green-50', border: 'border-green-200', label: 'Scăzut' },
  MEDIUM: { color: 'text-yellow-700', bg: 'bg-yellow-50', border: 'border-yellow-200', label: 'Mediu' },
  HIGH: { color: 'text-orange-700', bg: 'bg-orange-50', border: 'border-orange-200', label: 'Ridicat' },
  CRITICAL: { color: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200', label: 'Critic' },
};

const MOOD_LABELS: Record<string, string> = {
  GREAT: '😊 Excelent',
  GOOD: '🙂 Bun',
  OKAY: '😐 Neutru',
  BAD: '😟 Rău',
  TERRIBLE: '😰 Foarte rău',
};

const SLEEP_QUALITY_LABELS: Record<string, string> = {
  VERY_POOR: 'Foarte slab',
  POOR: 'Slab',
  FAIR: 'Acceptabil',
  GOOD: 'Bun',
  EXCELLENT: 'Excelent',
};

interface MonitorData {
  patient: {
    firstName: string;
    lastName: string;
    profilePhotoUrl: string | null;
    lifestyleMode: string | null;
    primaryCondition: string | null;
    diseaseStage: string | null;
    age: number | null;
    gender: string | null;
  };
  role: string;
  permissions: Record<string, boolean>;
  period: { days: number; since: string };
  nutrition?: {
    daily: any[];
    goal: any;
    avgCalories: number;
    daysTracked: number;
  };
  medication?: {
    medications: any[];
    compliance: number | null;
    totalDoses: number;
    skippedDoses: number;
  };
  symptoms?: {
    painLogs: any[];
    avgPainLevel: number | null;
    maxPainLevel: number | null;
  };
  sleep?: {
    logs: any[];
    avgDuration: number | null;
    daysTracked: number;
  };
  vitals?: {
    logs: any[];
    latest: any | null;
  };
  mood?: {
    stressLogs: any[];
    checkIns: any[];
  };
  alerts: any[];
}

export default function MonitorDashboardPage() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<MonitorData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [days, setDays] = useState(7);
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [resolvedAlerts, setResolvedAlerts] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (token) loadData();
  }, [token, days]);

  async function loadData() {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/api/care-circle/monitor/${token}/data?days=${days}`);
      setData(res.data);
    } catch (err: any) {
      const msg = err.response?.data?.error || 'Eroare la încărcarea datelor';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  async function resolveAlert(alertId: string) {
    try {
      await axios.put(`${API}/api/care-circle/monitor/${token}/alerts/${alertId}/resolve`);
      setResolvedAlerts(prev => new Set([...prev, alertId]));
    } catch {}
  }

  function toggleSection(section: string) {
    setExpandedSection(expandedSection === section ? null : section);
  }

  function formatDate(dateStr: string) {
    return new Date(dateStr).toLocaleDateString('ro-RO', { day: 'numeric', month: 'short' });
  }

  function formatDateTime(dateStr: string) {
    return new Date(dateStr).toLocaleString('ro-RO', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500">Se încarcă datele de monitorizare...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center shadow-lg">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Acces Indisponibil</h2>
          <p className="text-gray-500 text-sm leading-relaxed">{error}</p>
          <p className="text-gray-400 text-xs mt-4">
            Dacă crezi că este o eroare, contactează persoana care ți-a trimis link-ul.
          </p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { patient, permissions, alerts } = data;
  const patientName = `${patient.firstName || ''} ${patient.lastName || ''}`.trim() || 'Utilizator';
  const activeAlerts = alerts.filter(a => !a.isResolved && !resolvedAlerts.has(a.id));
  const criticalAlerts = activeAlerts.filter(a => a.severity === 'CRITICAL' || a.severity === 'HIGH');

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-700 text-white">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center text-xl font-bold">
                {patient.firstName?.[0] || '?'}
              </div>
              <div>
                <h1 className="text-xl font-bold">{patientName}</h1>
                <p className="text-blue-100 text-sm">
                  {patient.age && `${patient.age} ani · `}
                  {patient.primaryCondition || 'Profil de sănătate'}
                </p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs text-blue-200 mb-1">Monitorizezi ca</div>
              <div className="text-sm font-semibold bg-white/20 px-3 py-1 rounded-full">
                {ROLE_LABELS[data.role] || data.role}
              </div>
            </div>
          </div>

          {/* Alerte critice */}
          {criticalAlerts.length > 0 && (
            <div className="bg-red-500/30 border border-red-300/50 rounded-xl p-4 mb-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-5 h-5 text-red-200" />
                <span className="font-semibold text-white">{criticalAlerts.length} alertă{criticalAlerts.length !== 1 ? 'e' : ''} importantă{criticalAlerts.length !== 1 ? 'e' : ''}</span>
              </div>
              {criticalAlerts.slice(0, 2).map(alert => (
                <div key={alert.id} className="text-sm text-red-100 mb-1">• {alert.title}: {alert.message}</div>
              ))}
            </div>
          )}

          {/* Selector perioadă */}
          <div className="flex items-center gap-2">
            <span className="text-blue-200 text-sm">Perioadă:</span>
            {[7, 14, 30].map(d => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={`px-3 py-1 rounded-full text-sm font-medium transition ${
                  days === d ? 'bg-white text-blue-700' : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                {d} zile
              </button>
            ))}
            <button onClick={loadData} className="ml-auto p-2 bg-white/20 rounded-full hover:bg-white/30 transition">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">

        {/* Alerte */}
        {activeAlerts.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <button
              onClick={() => toggleSection('alerts')}
              className="w-full flex items-center justify-between p-5 hover:bg-gray-50 transition"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-50 rounded-xl">
                  <Bell className="w-5 h-5 text-red-500" />
                </div>
                <div className="text-left">
                  <h3 className="font-semibold text-gray-900">Alerte Active</h3>
                  <p className="text-sm text-gray-500">{activeAlerts.length} alertă{activeAlerts.length !== 1 ? 'e' : ''} nerezolvată{activeAlerts.length !== 1 ? 'e' : ''}</p>
                </div>
              </div>
              {expandedSection === 'alerts' ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
            </button>

            {expandedSection === 'alerts' && (
              <div className="border-t border-gray-100 p-5 space-y-3">
                {activeAlerts.map(alert => {
                  const sev = SEVERITY_CONFIG[alert.severity] || SEVERITY_CONFIG.MEDIUM;
                  return (
                    <div key={alert.id} className={`p-4 rounded-xl border ${sev.bg} ${sev.border}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${sev.bg} ${sev.color} border ${sev.border}`}>
                              {sev.label}
                            </span>
                            <span className={`font-semibold text-sm ${sev.color}`}>{alert.title}</span>
                          </div>
                          <p className="text-sm text-gray-600">{alert.message}</p>
                          <p className="text-xs text-gray-400 mt-1">{formatDateTime(alert.createdAt)}</p>
                        </div>
                        <button
                          onClick={() => resolveAlert(alert.id)}
                          className="flex-shrink-0 flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs text-gray-600 hover:bg-gray-50 transition"
                        >
                          <CheckCircle className="w-3 h-3" />
                          Rezolvat
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Sumar rapid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {permissions.canViewNutrition && data.nutrition && (
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
              <div className="flex items-center gap-2 mb-2">
                <Utensils className="w-4 h-4 text-orange-500" />
                <span className="text-xs font-medium text-gray-500">Calorii/zi</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{data.nutrition.avgCalories || 0}</p>
              <p className="text-xs text-gray-400">{data.nutrition.daysTracked} zile înregistrate</p>
              {data.nutrition.goal && (
                <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-orange-400 rounded-full"
                    style={{ width: `${Math.min((data.nutrition.avgCalories / data.nutrition.goal.targetCalories) * 100, 100)}%` }}
                  />
                </div>
              )}
            </div>
          )}

          {permissions.canViewMedication && data.medication && (
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
              <div className="flex items-center gap-2 mb-2">
                <Pill className="w-4 h-4 text-blue-500" />
                <span className="text-xs font-medium text-gray-500">Complianță</span>
              </div>
              <p className={`text-2xl font-bold ${
                data.medication.compliance === null ? 'text-gray-400' :
                data.medication.compliance >= 80 ? 'text-green-600' :
                data.medication.compliance >= 60 ? 'text-yellow-600' : 'text-red-600'
              }`}>
                {data.medication.compliance !== null ? `${data.medication.compliance}%` : 'N/A'}
              </p>
              <p className="text-xs text-gray-400">{data.medication.medications.length} medicamente active</p>
            </div>
          )}

          {permissions.canViewSleep && data.sleep && (
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
              <div className="flex items-center gap-2 mb-2">
                <Moon className="w-4 h-4 text-indigo-500" />
                <span className="text-xs font-medium text-gray-500">Somn mediu</span>
              </div>
              <p className={`text-2xl font-bold ${
                data.sleep.avgDuration === null ? 'text-gray-400' :
                data.sleep.avgDuration >= 7 ? 'text-green-600' :
                data.sleep.avgDuration >= 5 ? 'text-yellow-600' : 'text-red-600'
              }`}>
                {data.sleep.avgDuration !== null ? `${data.sleep.avgDuration}h` : 'N/A'}
              </p>
              <p className="text-xs text-gray-400">{data.sleep.daysTracked} nopți înregistrate</p>
            </div>
          )}

          {permissions.canViewSymptoms && data.symptoms && (
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
              <div className="flex items-center gap-2 mb-2">
                <Activity className="w-4 h-4 text-red-500" />
                <span className="text-xs font-medium text-gray-500">Durere medie</span>
              </div>
              <p className={`text-2xl font-bold ${
                data.symptoms.avgPainLevel === null ? 'text-gray-400' :
                data.symptoms.avgPainLevel <= 3 ? 'text-green-600' :
                data.symptoms.avgPainLevel <= 6 ? 'text-yellow-600' : 'text-red-600'
              }`}>
                {data.symptoms.avgPainLevel !== null ? `${data.symptoms.avgPainLevel}/10` : 'N/A'}
              </p>
              <p className="text-xs text-gray-400">Max: {data.symptoms.maxPainLevel ?? 'N/A'}/10</p>
            </div>
          )}

          {permissions.canViewVitals && data.vitals?.latest && (
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
              <div className="flex items-center gap-2 mb-2">
                <Heart className="w-4 h-4 text-pink-500" />
                <span className="text-xs font-medium text-gray-500">Puls</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {data.vitals.latest.heartRate ? `${data.vitals.latest.heartRate} bpm` : 'N/A'}
              </p>
              <p className="text-xs text-gray-400">
                {data.vitals.latest.systolicBP && data.vitals.latest.diastolicBP
                  ? `TA: ${data.vitals.latest.systolicBP}/${data.vitals.latest.diastolicBP}`
                  : 'Ultima măsurătoare'}
              </p>
            </div>
          )}

          {permissions.canViewMood && data.mood && (
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
              <div className="flex items-center gap-2 mb-2">
                <Smile className="w-4 h-4 text-yellow-500" />
                <span className="text-xs font-medium text-gray-500">Stare emoțională</span>
              </div>
              <p className="text-lg font-bold text-gray-900">
                {data.mood.checkIns[0] ? MOOD_LABELS[data.mood.checkIns[0].mood] || data.mood.checkIns[0].mood : 'N/A'}
              </p>
              <p className="text-xs text-gray-400">{data.mood.checkIns.length} check-in-uri</p>
            </div>
          )}
        </div>

        {/* Secțiuni detaliate */}

        {/* Medicamente */}
        {permissions.canViewMedication && data.medication && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <button
              onClick={() => toggleSection('medication')}
              className="w-full flex items-center justify-between p-5 hover:bg-gray-50 transition"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-50 rounded-xl">
                  <Pill className="w-5 h-5 text-blue-500" />
                </div>
                <div className="text-left">
                  <h3 className="font-semibold text-gray-900">Medicamente</h3>
                  <p className="text-sm text-gray-500">
                    Complianță: {data.medication.compliance !== null ? `${data.medication.compliance}%` : 'Fără date'}
                    {data.medication.skippedDoses > 0 && ` · ${data.medication.skippedDoses} doze omise`}
                  </p>
                </div>
              </div>
              {expandedSection === 'medication' ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
            </button>

            {expandedSection === 'medication' && (
              <div className="border-t border-gray-100 p-5 space-y-4">
                {data.medication.medications.map((med: any, idx: number) => (
                  <div key={idx} className="border border-gray-100 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <h4 className="font-semibold text-gray-900">{med.name}</h4>
                        <p className="text-sm text-gray-500">{med.dose} · {med.frequency}</p>
                      </div>
                    </div>
                    {med.recentLogs.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {med.recentLogs.slice(0, 7).map((log: any, i: number) => (
                          <div
                            key={i}
                            title={formatDateTime(log.takenAt)}
                            className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold ${
                              log.skipped ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'
                            }`}
                          >
                            {log.skipped ? '✗' : '✓'}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Somn */}
        {permissions.canViewSleep && data.sleep && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <button
              onClick={() => toggleSection('sleep')}
              className="w-full flex items-center justify-between p-5 hover:bg-gray-50 transition"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-indigo-50 rounded-xl">
                  <Moon className="w-5 h-5 text-indigo-500" />
                </div>
                <div className="text-left">
                  <h3 className="font-semibold text-gray-900">Somn</h3>
                  <p className="text-sm text-gray-500">
                    Medie: {data.sleep.avgDuration !== null ? `${data.sleep.avgDuration}h/noapte` : 'Fără date'}
                  </p>
                </div>
              </div>
              {expandedSection === 'sleep' ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
            </button>

            {expandedSection === 'sleep' && (
              <div className="border-t border-gray-100 p-5">
                <div className="space-y-2">
                  {data.sleep.logs.map((log: any, idx: number) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                      <span className="text-sm text-gray-600">{formatDate(log.date)}</span>
                      <div className="flex items-center gap-3">
                        <span className={`text-sm font-semibold ${
                          (log.duration || 0) >= 7 ? 'text-green-600' :
                          (log.duration || 0) >= 5 ? 'text-yellow-600' : 'text-red-600'
                        }`}>
                          {log.duration ? `${log.duration}h` : 'N/A'}
                        </span>
                        <span className="text-xs text-gray-400">{SLEEP_QUALITY_LABELS[log.quality] || log.quality}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Simptome */}
        {permissions.canViewSymptoms && data.symptoms && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <button
              onClick={() => toggleSection('symptoms')}
              className="w-full flex items-center justify-between p-5 hover:bg-gray-50 transition"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-50 rounded-xl">
                  <Activity className="w-5 h-5 text-red-500" />
                </div>
                <div className="text-left">
                  <h3 className="font-semibold text-gray-900">Simptome & Durere</h3>
                  <p className="text-sm text-gray-500">
                    {data.symptoms.painLogs.length} înregistrări · Max: {data.symptoms.maxPainLevel ?? 'N/A'}/10
                  </p>
                </div>
              </div>
              {expandedSection === 'symptoms' ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
            </button>

            {expandedSection === 'symptoms' && (
              <div className="border-t border-gray-100 p-5">
                <div className="space-y-2">
                  {data.symptoms.painLogs.map((log: any, idx: number) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                      <div>
                        <span className="text-sm text-gray-600">{formatDate(log.date)}</span>
                        {log.location?.length > 0 && (
                          <p className="text-xs text-gray-400">{log.location.join(', ')}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
                          log.painLevel <= 3 ? 'bg-green-100 text-green-700' :
                          log.painLevel <= 6 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {log.painLevel}
                        </div>
                        <span className="text-xs text-gray-400">/10</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Vitale */}
        {permissions.canViewVitals && data.vitals && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <button
              onClick={() => toggleSection('vitals')}
              className="w-full flex items-center justify-between p-5 hover:bg-gray-50 transition"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-pink-50 rounded-xl">
                  <Heart className="w-5 h-5 text-pink-500" />
                </div>
                <div className="text-left">
                  <h3 className="font-semibold text-gray-900">Semne Vitale</h3>
                  <p className="text-sm text-gray-500">
                    {data.vitals.latest
                      ? `Ultima: ${formatDate(data.vitals.latest.date)}`
                      : 'Fără date recente'}
                  </p>
                </div>
              </div>
              {expandedSection === 'vitals' ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
            </button>

            {expandedSection === 'vitals' && (
              <div className="border-t border-gray-100 p-5">
                {data.vitals.latest && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
                    {data.vitals.latest.heartRate && (
                      <div className="p-3 bg-pink-50 rounded-xl text-center">
                        <p className="text-xs text-gray-500 mb-1">Puls</p>
                        <p className="text-xl font-bold text-pink-700">{data.vitals.latest.heartRate}</p>
                        <p className="text-xs text-gray-400">bpm</p>
                      </div>
                    )}
                    {data.vitals.latest.systolicBP && (
                      <div className="p-3 bg-blue-50 rounded-xl text-center">
                        <p className="text-xs text-gray-500 mb-1">Tensiune</p>
                        <p className="text-xl font-bold text-blue-700">{data.vitals.latest.systolicBP}/{data.vitals.latest.diastolicBP}</p>
                        <p className="text-xs text-gray-400">mmHg</p>
                      </div>
                    )}
                    {data.vitals.latest.glucose && (
                      <div className="p-3 bg-orange-50 rounded-xl text-center">
                        <p className="text-xs text-gray-500 mb-1">Glicemie</p>
                        <p className="text-xl font-bold text-orange-700">{data.vitals.latest.glucose}</p>
                        <p className="text-xs text-gray-400">mg/dL</p>
                      </div>
                    )}
                    {data.vitals.latest.weight && (
                      <div className="p-3 bg-green-50 rounded-xl text-center">
                        <p className="text-xs text-gray-500 mb-1">Greutate</p>
                        <p className="text-xl font-bold text-green-700">{data.vitals.latest.weight}</p>
                        <p className="text-xs text-gray-400">kg</p>
                      </div>
                    )}
                    {data.vitals.latest.oxygenSat && (
                      <div className="p-3 bg-cyan-50 rounded-xl text-center">
                        <p className="text-xs text-gray-500 mb-1">SpO2</p>
                        <p className="text-xl font-bold text-cyan-700">{data.vitals.latest.oxygenSat}%</p>
                        <p className="text-xs text-gray-400">saturație O2</p>
                      </div>
                    )}
                  </div>
                )}

                <div className="space-y-2">
                  {data.vitals.logs.slice(0, 5).map((log: any, idx: number) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl text-sm">
                      <span className="text-gray-500">{formatDate(log.date)}</span>
                      <div className="flex gap-3 text-gray-700">
                        {log.heartRate && <span>❤️ {log.heartRate} bpm</span>}
                        {log.systolicBP && <span>🩺 {log.systolicBP}/{log.diastolicBP}</span>}
                        {log.glucose && <span>🩸 {log.glucose} mg/dL</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Alimentație */}
        {permissions.canViewNutrition && data.nutrition && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <button
              onClick={() => toggleSection('nutrition')}
              className="w-full flex items-center justify-between p-5 hover:bg-gray-50 transition"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-50 rounded-xl">
                  <Utensils className="w-5 h-5 text-orange-500" />
                </div>
                <div className="text-left">
                  <h3 className="font-semibold text-gray-900">Alimentație</h3>
                  <p className="text-sm text-gray-500">
                    Medie: {data.nutrition.avgCalories} kcal/zi · {data.nutrition.daysTracked} zile înregistrate
                  </p>
                </div>
              </div>
              {expandedSection === 'nutrition' ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
            </button>

            {expandedSection === 'nutrition' && (
              <div className="border-t border-gray-100 p-5">
                <div className="space-y-2">
                  {data.nutrition.daily.map((day: any, idx: number) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                      <span className="text-sm text-gray-600">{formatDate(day.date)}</span>
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-semibold text-gray-900">{Math.round(day.totalCalories)} kcal</span>
                        {data.nutrition?.goal && (
                          <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full ${
                                day.totalCalories >= data.nutrition.goal.targetCalories * 0.9 ? 'bg-green-400' : 'bg-orange-400'
                              }`}
                              style={{ width: `${Math.min((day.totalCalories / data.nutrition.goal.targetCalories) * 100, 100)}%` }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="text-center py-6">
          <div className="flex items-center justify-center gap-2 text-gray-400 text-xs mb-2">
            <Shield className="w-3 h-3" />
            <span>Date protejate · Acces autorizat de {patientName}</span>
          </div>
          <p className="text-gray-300 text-xs">
            FII FIT · Platforma ta de fitness · fiifit.ro
          </p>
        </div>
      </div>
    </div>
  );
}
